///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { GtthProgressWindowModel } from './progress-window.model';
import { Router } from '@angular/router';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { ProjectWindowsEvents } from '../project-windows-events.model';
import { EngineExceptionDTO } from '../../../generated/shared-types.model';
import { GtthEventManager } from '../../../services/event-manager.service';
import { GtthNotificationsService } from '../../logical-topology/engine/notifications-service';
import { GtthProjectUserSessionService } from '../../logical-topology/engine/project-user-session.service';

@Injectable({
    providedIn: 'root'
})
export class GtthProgressWindowService {
    public static readonly BU_DEVICE_ORIENTATION_COLLISION_ERROR = 'BU device orientation collision';
    private readonly progressWindowSource = new Subject<GtthProgressWindowModel>();
    public progressWindow$ = this.progressWindowSource.asObservable();

    constructor(
        private readonly router: Router,
        private readonly eventManager: GtthEventManager,
        private readonly notificationService: GtthNotificationsService,
        private readonly projectUserSessionService: GtthProjectUserSessionService
    ) {}

    public _historyService: GtthHistoryService;

    get historyService(): GtthHistoryService {
        return this._historyService;
    }

    set historyService(value: GtthHistoryService) {
        this._historyService = value;
    }

    public show(windowModel: GtthProgressWindowModel) {
        if (windowModel.error) {
            this.handleDesignError(windowModel.error);
        }

        if (!windowModel.isCommandFinished()) {
            this.handleDesignProgress(windowModel);
            return;
        }

        if (windowModel.commandName === GtthProgressWindowModel.MULTIPLE_DESIGN && !windowModel.isInterrupted()) {
            this.handleDesignProgress(windowModel);
        } else {
            this.hide();
        }
    }

    handleDesignProgress(windowModel: GtthProgressWindowModel) {
        if (!this?._historyService?.logicalTopology) {
            return;
        }

        if (
            windowModel.commandName === GtthProgressWindowModel.MULTIPLE_DESIGN &&
            windowModel?.progress?.designIterationsChartData != null &&
            (!this._historyService.logicalTopology.designIterationsChartData ||
                this._historyService.logicalTopology.designIterationsChartData.length !==
                    windowModel.progress.designIterationsChartData.length)
        ) {
            this._historyService.logicalTopology.designIterationsChartData = windowModel.progress.designIterationsChartData;
        }

        this.projectUserSessionService.setShowDesignIterations(true);
        this.progressWindowSource.next(windowModel);
    }

    handleDesignError(designError: EngineExceptionDTO) {
        this.eventManager.broadcast({ name: ProjectWindowsEvents.CLOSE_MODAL_WINDOW });

        if (designError.topologyValidity) {
            this.historyService.reportsService.setTopologyValidityToCache(designError.topologyValidity);
        }

        if (
            designError.message.startsWith(GtthProgressWindowService.BU_DEVICE_ORIENTATION_COLLISION_ERROR) ||
            designError.message.startsWith('Found nodes with no links') ||
            designError.message.startsWith('Some nodes cannot be connected to POP') ||
            designError.message.startsWith('Some nodes (1) cannot be connected to POP') ||
            designError.message.startsWith('Mesh not connected')
        ) {
            // if Design Rings'
            setTimeout(() => {
                this.router.navigate([{ outlets: { popup: `project-view/connected-sites` } }], { replaceUrl: true });
            }, 200);
        } else if (designError.message.startsWith('Sector') && designError.message.indexOf('overloaded with')) {
            designError.message = 'Some sectors are overloaded with frozen links';

            setTimeout(() => {
                this.router.navigate([{ outlets: { popup: `project-view/overloaded-sectors` } }], { replaceUrl: true });
            }, 200);
        } else if (designError.message.startsWith('Node') && designError.message.indexOf('overloaded with')) {
            //    do nothing
        } else if (
            designError?.topologyValidity?.autoConfigRingsErrorNodes &&
            designError?.topologyValidity?.autoConfigRingsErrorNodes.length > 0
        ) {
            // 'Auto configure rings'
            this._historyService.autoConfigRingsErrorNodes = designError.topologyValidity.autoConfigRingsErrorNodes;
            const errorModalTitle =
                /Cannot complete PtMP design/.exec(designError.message) ||
                /Some nodes are prevented from connecting by PtMP/.exec(designError.message)
                    ? 'p2mp-design'
                    : 'config-rings';
            setTimeout(() => {
                this.router.navigate([{ outlets: { popup: `project-view/nodes-design-error/${errorModalTitle}` } }], { replaceUrl: true });
            }, 200);
        }

        this.notificationService.showError(designError.message);
    }

    public hide() {
        this.progressWindowSource.next(undefined);
    }
}
